import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

/**
 * STEAM Studios Landing — Art Director Cut (Scrollytelling + Cinematic Hero)
 * Palette: Weld Blue (#1F4FFF) & Sodium-Vapor Amber (#FFB300)
 * Type: League Spartan (display) + system sans (body)
 */

// -------------------- DATA --------------------
const TEAM = [
  {
    name: "Noah Sheinbaum",
    role: "Business & Operations",
    bio:
      "Entrepreneur and operator, repeat founder, and former political campaign manager supporting the narrative infrastructure for America's industrial renaissance.",
    img: "/media/Noah.jpg",
  },
  {
    name: "Tennessee Edwards",
    role: "Creative",
    bio:
      "Creative director, 20+ year Hollywood veteran producing top tier reality TV. Lifelong creative and Renaissance Man.",
    img: "/media/Tennessee.jpg",
  },
];

const WORK_TILES = [
  {
    title: "The STEAM Zone",
    monogram: "SZ",
    image: "/media/The STEAM Zone.jpg",
    description: "Commentary and analysis from across science, tech, engineering, AI and manufacturing.",
    url: "https://thesteamzone.com"
  },
  {
    title: "Defense Gameday",
    monogram: "DG",
    image: "/media/Defense Gameday.jpg",
    description: "Live show, high-energy broadcast from the top defense and national security events.",
    url: "https://www.linkedin.com/company/defense-gameday"
  },
  {
    title: "Crossing The Valley",
    monogram: "CTV",
    image: "/media/Crossing The Valley Logo YT.png",
    description: "Case studies about tech moving from prototype to production in the federal market.",
    url: "https://valleycrossers.com"
  },
  {
    title: "US National Drone Association",
    monogram: "USNDA",
    image: "/media/USNDA.jpg",
    description: "501c3 supporting America's national drone dominance.",
    url: "https://www.usnda.org/"
  },
  {
    title: "Bazze",
    monogram: "BZ",
    image: "/media/bazze_final.jpg",
    description: "Data marketplace for defense and commercial customers.",
    url: "https://bazze.io"
  },
  {
    title: "Rebuilding the Fleet",
    monogram: "FR",
    image: "/media/Rebuilding the Fleet - v2.png",
    description: "Shipbuilding industry commentary and analysis.",
    url: "https://www.linkedin.com/company/rebuilding-the-fleet"
  },
];

export default function SteamStudiosLanding() {
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.4], [0, -80]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.35], [1, 0.35]);
  const [showSteam, setShowSteam] = useState(false);

  // Steam animation trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSteam(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#0A0B0D] text-zinc-100 [--acc1:#1F4FFF] [--acc2:#FFB300] selection:bg-zinc-100 selection:text-black">
      <Grain />
      <SteamOverlay show={showSteam} />

      {/* HERO: full-bleed cinematic video with oversized wordmark */}
      <section className="relative isolate flex h-[110svh] items-center justify-center overflow-hidden">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          playsInline
          loop
          preload="auto"
        >
          <source src="/media/steam.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />

        {/* Ambient glows keyed to palette */}
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div
            className="absolute -left-24 top-10 h-80 w-80 rounded-full blur-3xl"
            style={{ background: "radial-gradient(closest-side, var(--acc1), transparent 70%)" }}
          />
          <div
            className="absolute -right-24 bottom-10 h-[28rem] w-[28rem] rounded-full blur-[90px]"
            style={{ background: "radial-gradient(closest-side, var(--acc2), transparent 70%)" }}
          />
        </div>

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 w-full max-w-[1400px] px-6">
          <WordmarkOverVideo />
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="mt-6 text-center text-[clamp(14px,1.4vw,20px)] uppercase tracking-[0.28em] text-zinc-100/90"
          >
            We fight for story.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mx-auto mt-10 w-fit rounded-full border border-white/15 bg-black/30 px-4 py-2 text-[12px] uppercase tracking-[0.18em] backdrop-blur-sm"
          >
            <span className="mr-3 inline-block h-2 w-2 animate-pulse rounded-full bg-white/80" />
            Scroll to enter
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="mx-auto mt-8 flex justify-center gap-4 flex-wrap"
          >
            <Link
              to="/work-with-us"
              className="group relative overflow-hidden rounded-full border border-white/25 bg-white/[0.08] px-10 py-5 text-[16px] font-bold tracking-wide text-zinc-100 transition-all duration-300 hover:border-white/50 hover:bg-white/[0.15] hover:scale-105 backdrop-blur-md"
            >
              <div className="pointer-events-none absolute -left-8 top-1/2 h-16 w-16 -translate-y-1/2 rounded-full blur-xl opacity-50 transition-opacity duration-300 group-hover:opacity-70" style={{ background: "radial-gradient(closest-side, var(--acc1), transparent)" }} />
              <div className="pointer-events-none absolute -right-8 top-1/2 h-16 w-16 -translate-y-1/2 rounded-full blur-xl opacity-40 transition-opacity duration-300 group-hover:opacity-60" style={{ background: "radial-gradient(closest-side, var(--acc2), transparent)" }} />
              <span className="relative z-10">Work With Us</span>
            </Link>
            <a
              href="mailto:Contact@steamstudio.io"
              className="group relative overflow-hidden rounded-full border border-white/20 bg-transparent px-10 py-5 text-[16px] font-semibold tracking-wide text-zinc-100 transition-all duration-300 hover:border-white/40 hover:bg-white/[0.05] hover:scale-105 backdrop-blur-md"
            >
              <span className="relative z-10">Get in Touch</span>
            </a>
          </motion.div>
        </motion.div>

        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </section>

      {/* WHO WE ARE */}
      <Section id="who" title="Who we are">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
          {TEAM.map((p) => (
            <PersonCard key={p.name} {...p} />
          ))}
        </div>
      </Section>

      {/* WHAT WE DO — Scrollytelling */}
      <ScrollyStudios />

      {/* OUR WORK */}
      <Section id="work" title="Our Work" kicker="Selected Partners">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {WORK_TILES.map((w) => (
            <WorkTile key={w.title} {...w} />
          ))}
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="mx-auto max-w-7xl px-6 py-16 text-zinc-400/80">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <p className="text-xs">© {new Date().getFullYear()} STEAM Studios</p>
          <nav className="flex gap-6 text-xs">
            <a href="#who" className="hover:text-zinc-100 transition">Who we are</a>
            <a href="#what" className="hover:text-zinc-100 transition">What we do</a>
            <a href="#work" className="hover:text-zinc-100 transition">Our Work</a>
            <Link to="/work-with-us" className="hover:text-zinc-100 transition">Work With Us</Link>
          </nav>
        </div>
      </footer>

      <StyleBlocks />
    </div>
  );
}

// -------------------- COMPONENTS --------------------

function SteamOverlay({ show }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: "100vh", opacity: 0, scale: 0.8 }}
          animate={{ y: 0, opacity: 0.6, scale: 1 }}
          exit={{ y: "-100vh", opacity: 0 }}
          transition={{ 
            duration: 3, 
            ease: [0.25, 0.46, 0.45, 0.94],
            opacity: { duration: 2 }
          }}
          className="fixed inset-0 pointer-events-none z-[100] mix-blend-screen"
          style={{
            background: `
              radial-gradient(ellipse at 50% 100%, rgba(255, 255, 255, 0.1) 0%, transparent 70%),
              radial-gradient(ellipse at 30% 80%, rgba(31, 79, 255, 0.05) 0%, transparent 50%),
              radial-gradient(ellipse at 70% 80%, rgba(255, 179, 0, 0.05) 0%, transparent 50%)
            `
          }}
        />
      )}
    </AnimatePresence>
  );
}

function Section({ id, title, kicker, children }) {
  return (
    <section id={id} className="relative mx-auto max-w-7xl px-6 py-0 md:py-0">
      <div className="sticky top-0 z-10 mx-auto bg-gradient-to-b from-[#0A0B0D] via-[#0A0B0D]/70 to-transparent py-12 md:py-16">
        {kicker && (
          <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-3 text-[12px] uppercase tracking-[0.22em] text-zinc-400/80">
            {kicker}
          </motion.div>
        )}
        <TitleSweep>{title}</TitleSweep>
      </div>
      <div className="pb-24">
        {children}
      </div>
    </section>
  );
}

function TitleSweep({ children }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ 
    target: ref, 
    offset: ["start 80%", "center 40%", "end 20%"] 
  });
  
  // Enhanced animation: right → center → left with better timing
  const bgX = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], ["150%", "50%", "0%", "-150%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.5, 0.85, 1], [0.5, 0.8, 1, 0.8, 0.6]);
  const brightness = useTransform(scrollYProgress, [0, 0.3, 0.5, 0.7, 1], [1.8, 2.2, 2.5, 2.2, 1.8]);

  return (
    <motion.h2
      ref={ref}
      style={{ 
        backgroundPositionX: bgX, 
        opacity,
        filter: `brightness(${brightness.get()})`,
        WebkitTextStroke: "0.5px rgba(255,255,255,0.15)" 
      }}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="text-balance text-[clamp(32px,6vw,64px)] font-black leading-[0.95] tracking-tight bg-[linear-gradient(90deg,theme(colors.zinc.600),white,theme(colors.zinc.600))] bg-[length:300%_100%] bg-clip-text text-transparent font-spartan"
    >
      {children}
    </motion.h2>
  );
}

function PersonCard({ name, role, bio, img }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.3 }}
      className="group relative overflow-hidden rounded-3xl border border-white/8 bg-white/[0.03] p-6 md:p-8"
    >
      {/* light diagonal accent */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 -rotate-12 rounded-full blur-[70px] opacity-30" style={{ background: "radial-gradient(closest-side, var(--acc1), transparent 70%)" }} />
      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
        <div>
          <h3 className="text-[clamp(28px,3.4vw,44px)] font-extrabold tracking-tight leading-[1.05] font-spartan">
            {name}
          </h3>
          <p className="mt-1 text-[clamp(13px,1.2vw,16px)] text-zinc-300/90">{role}</p>
          <p className="mt-4 text-[clamp(13px,1.15vw,16px)] text-zinc-300/80 max-w-prose">{bio}</p>
        </div>
        <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl border border-white/10">
          <img src={img} alt={`${name} portrait`} className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
          <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10" />
        </div>
      </div>
    </motion.article>
  );
}

function ScrollyStudios() {
  return (
    <section id="what" className="relative mx-auto max-w-7xl px-6 py-0 md:py-0">
      <div className="sticky top-0 z-10 mx-auto bg-gradient-to-b from-[#0A0B0D] via-[#0A0B0D]/70 to-transparent py-12 md:py-16">
        <h2 className="text-balance text-[clamp(32px,6vw,64px)] font-black leading-[0.95] tracking-tight font-spartan" style={{ filter: 'brightness(2.2)' }}>What we do</h2>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 pb-12">
        <StudioCard
          heading="Narrative Studio"
          sub="From Art to Action."
          copy="We architect stories that move markets—brand systems, films, and live experiences tuned for impact and adoption."
          accent="var(--acc2)"
        />
        <StudioCard
          heading="Product Studio"
          sub="Technology for Humans."
          copy="Practical tools for storytellers and operators. Calm interfaces, sharp analytics, and distribution built in."
          accent="var(--acc1)"
        />
      </div>
      <div className="flex justify-center pb-24">
        <motion.a
          href="mailto:Contact@steamstudio.io"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="group relative overflow-hidden rounded-full border border-white/20 bg-white/[0.05] px-8 py-4 text-[14px] font-semibold tracking-wide text-zinc-200 transition-all duration-300 hover:border-white/40 hover:bg-white/[0.08] hover:scale-105 backdrop-blur-sm"
        >
          <div className="pointer-events-none absolute -left-6 top-1/2 h-12 w-12 -translate-y-1/2 rounded-full blur-xl opacity-40 transition-opacity duration-300 group-hover:opacity-60" style={{ background: "radial-gradient(closest-side, var(--acc1), transparent)" }} />
          Get in Touch
        </motion.a>
      </div>
    </section>
  );
}

function StudioCard({ heading, sub, copy, accent }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.3 }}
      className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8"
      style={{ backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))` }}
    >
      <div className="pointer-events-none absolute -left-24 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full blur-[90px] opacity-30" style={{ background: `radial-gradient(closest-side, ${accent}, transparent 72%)` }} />
      <h3 className="text-[clamp(24px,3.2vw,42px)] font-extrabold tracking-tight leading-tight font-spartan">{heading}</h3>
      <p className="mt-2 text-[clamp(13px,1.2vw,16px)] text-zinc-300/90">{sub}</p>
      <p className="mt-4 max-w-prose text-[clamp(13px,1.15vw,16px)] text-zinc-300/80">{copy}</p>
    </motion.article>
  );
}

function WorkTile({ title, monogram, image, description, url }) {
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true, amount: 0.2 }}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] hover:border-white/20 transition-all duration-500 cursor-pointer"
    >
      {/* Background image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={image}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Overlay content */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-xl border border-white/25 bg-black/40 backdrop-blur-sm text-xl font-black tracking-widest font-spartan">
              {monogram}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-[clamp(16px,1.4vw,20px)] font-bold text-zinc-200/90 font-spartan mb-2">
          {title}
        </h3>
        <p className="text-[clamp(12px,1.1vw,14px)] text-zinc-400/80">
          {description}
        </p>
      </div>

      <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/10" />
    </motion.a>
  );
}

// -------------------- HERO WORDMARK --------------------
function WordmarkOverVideo() {
  return (
    <div className="relative">
      <motion.h1
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        className="mx-auto max-w-none text-center font-black tracking-tight leading-[0.85] text-transparent [text-shadow:0_0_32px_rgba(0,0,0,0.45)] font-spartan"
        style={{ fontSize: "clamp(64px, 14vw, 220px)", WebkitTextStroke: "1.5px rgba(255,255,255,0.9)" }}
      >
        STEAM STUDIOS
      </motion.h1>
    </div>
  );
}

// -------------------- STYLES & UTILS --------------------
function Grain() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-[1] opacity-[0.07] mix-blend-soft-light" style={{ backgroundImage: `url('data:image/svg+xml;utf8,${encodeURIComponent(GRAIN_SVG)}')` }} />
  );
}

function StyleBlocks() {
  return (
    <style>{`
      /* Headline reveal */
      h2 { 
        -webkit-mask-image: linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%); 
        -webkit-mask-size: 200% 100%; 
        -webkit-mask-position: 100% 0%; 
        animation: h2reveal 1.6s ease-out both; 
      }
      
      @keyframes h2reveal { 
        to { -webkit-mask-position: 0% 0%; } 
      }
      
      :root { 
        --tight: -0.02em; 
      }
      
      h1, h2, h3 { 
        letter-spacing: var(--tight); 
        font-family: 'League Spartan', Inter, ui-sans-serif, system-ui; 
      }
      
      html { 
        scroll-behavior: smooth; 
      }
      
      /* Steam overlay animation */
      .steam-overlay {
        background: radial-gradient(ellipse at 50% 100%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
      }
    `}</style>
  );
}

const GRAIN_SVG = `
<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'>
  <filter id='n'>
    <feTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/>
  </filter>
  <rect width='100%' height='100%' filter='url(%23n)' opacity='0.75'/>
</svg>`;